import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Loader2, Inbox } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState(null);

  useEffect(() => {
    if (user === false) navigate("/admin/login");
  }, [user, navigate]);

  useEffect(() => {
    if (user) {
      api.get("/admin/submissions")
        .then((res) => setSubmissions(res.data))
        .catch(() => setSubmissions([]));
    }
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  if (user === null || (user && submissions === null)) {
    return (
      <main className="min-h-screen bg-slate-50 flex items-center justify-center pt-20" data-testid="admin-loading">
        <Loader2 size={32} className="animate-spin text-navy" />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50 pt-32 pb-24" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <p className="font-head text-xs font-bold uppercase tracking-[0.25em] text-brand-dark mb-3">Admin</p>
            <h1 className="font-display uppercase text-navy text-4xl md:text-5xl" data-testid="admin-heading">Contact Submissions</h1>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-button"
            className="font-head font-bold uppercase tracking-[0.12em] text-xs border border-navy/20 text-navy px-6 py-3 inline-flex items-center gap-2 hover:bg-navy-ink hover:text-white transition-colors duration-300"
          >
            <LogOut size={16} /> Sign Out
          </button>
        </div>

        {submissions && submissions.length === 0 ? (
          <div className="bg-white border border-navy/10 p-16 flex flex-col items-center gap-4 text-center" data-testid="admin-empty-state">
            <Inbox size={40} className="text-slate-300" />
            <p className="text-slate-500 font-medium">No submissions yet. New messages from the Connect page will appear here.</p>
          </div>
        ) : (
          <div className="bg-white border border-navy/10 overflow-x-auto" data-testid="admin-submissions-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-navy-ink hover:bg-navy-ink">
                  <TableHead className="text-white font-head uppercase tracking-wider text-xs">Date</TableHead>
                  <TableHead className="text-white font-head uppercase tracking-wider text-xs">Name</TableHead>
                  <TableHead className="text-white font-head uppercase tracking-wider text-xs">Email</TableHead>
                  <TableHead className="text-white font-head uppercase tracking-wider text-xs">Phone</TableHead>
                  <TableHead className="text-white font-head uppercase tracking-wider text-xs">Role</TableHead>
                  <TableHead className="text-white font-head uppercase tracking-wider text-xs">Updates</TableHead>
                  <TableHead className="text-white font-head uppercase tracking-wider text-xs min-w-[16rem]">Message</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {submissions && submissions.map((s) => (
                  <TableRow key={s.id} data-testid={`submission-row-${s.id}`}>
                    <TableCell className="whitespace-nowrap text-slate-500 text-sm">
                      {new Date(s.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </TableCell>
                    <TableCell className="font-semibold text-navy">{s.name}</TableCell>
                    <TableCell className="text-sm">{s.email}</TableCell>
                    <TableCell className="text-sm">{s.phone || "—"}</TableCell>
                    <TableCell>
                      <span className="bg-brand/15 text-brand-dark font-head text-xs font-bold uppercase tracking-wider px-3 py-1">{s.role}</span>
                    </TableCell>
                    <TableCell className="text-sm">{s.keep_updated ? "Yes" : "No"}</TableCell>
                    <TableCell className="text-sm text-slate-600 max-w-sm">{s.message}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </main>
  );
}
